/**
 * The rate-limit policy behind `onForeground`, with no React Native import so
 * it can be exercised directly rather than scanned for.
 *
 * See `foreground.ts` for why the limit exists. In short: on web
 * `AppState`'s "active" is `document.visibilitychange`, so every glance at
 * another browser tab was replaying every foreground handler, four of which
 * each start network work.
 */

/** One minute, which is the cap this was asked for in. */
export const DEFAULT_MIN_INTERVAL_MS = 60_000;

/**
 * Keyed by job name at MODULE level, not per subscription, and that is load
 * bearing. Several of the subscribing effects have dependencies that churn
 * (`accessToken`, `notificationPreferences`), so a limiter held in a closure
 * would reset every time an effect re-ran — silently restoring the behaviour
 * this removes, in the one way no test would catch.
 */
const lastRunAt = new Map<string, number>();

/**
 * Ask for a turn, and TAKE it if granted — the record is written here rather
 * than left to the caller, because a check that has to be followed by a
 * separate "and now remember it" is a check somebody eventually forgets.
 *
 * Timed from the START of the previous run, not its completion: this is a
 * ceiling on how often a job may begin, and a slow response must not buy the
 * next foreground an earlier turn. (`resync-gate.ts` times from completion
 * instead, because it collapses a burst rather than capping a rate.)
 */
export function claimForeground(
  name: string,
  at: number,
  minIntervalMs: number = DEFAULT_MIN_INTERVAL_MS,
): boolean {
  const previous = lastRunAt.get(name);
  if (previous !== undefined && at - previous < minIntervalMs) return false;
  lastRunAt.set(name, at);
  return true;
}

/**
 * Record a run without asking — how a caller that has JUST done the work says
 * so, rather than being assumed to have done it.
 *
 * This was first written as an unconditional stamp inside `onForeground`, on
 * the reasoning that every caller reads once on mount in the same effect that
 * subscribes. That reasoning was already false in the commit that introduced
 * it: `auth.refreshUser` subscribes without reading, so its first foreground
 * was suppressed against a run that never happened.
 *
 * Which is the argument this module's own header makes about closures, turned
 * on itself — a rule each call site has to remember is one the next call site
 * forgets. So the default now fails toward DOING the work, and a caller opts
 * in to being counted as primed. A new subscriber that forgets costs one extra
 * run; under the old default it silently skipped one.
 */
export function markForegroundRan(name: string, at: number): void {
  lastRunAt.set(name, at);
}

/** Test-only: forget every job's last run. */
export function __resetForegroundLimiter(): void {
  lastRunAt.clear();
}
